import React, { useState, useEffect, Suspense } from "react"
import { deleteReview, fetchReviews } from "../../store"
import { useSelector, useDispatch } from "react-redux"
import ReactPaginate from "react-paginate"
import {
  ArrowBigLeftDashIcon,
  ArrowBigRightDashIcon,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"
import Rating from "../ui/Rating"
import AdminReviewsChart from "../ui/AdminReviewsChart"
import { Link } from "react-router-dom"
import Spinner from "../Spinner"
import "../styles/AdminReviewsPage.css"

function AdminReviewsPage() {
  const dispatch = useDispatch()
  const { reviews } = useSelector((state) => state.reviews)
  const [itemsPerPage, setItemsPerPage] = useState(24)
  const [itemOffset, setItemOffset] = useState(0)
  const [selectedFilter, setSelectedFilter] = useState(null)
  const [expandedReviews, setExpandedReviews] = useState(new Set())

  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews((prevExpandedReviews) => {
      const newExpandedReviews = new Set(prevExpandedReviews)
      if (newExpandedReviews.has(reviewId)) {
        newExpandedReviews.delete(reviewId)
      } else {
        newExpandedReviews.add(reviewId)
      }
      return newExpandedReviews
    })
  }

  useEffect(() => {
    dispatch(fetchReviews())
  }, [])

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % reviews.length
    setItemOffset(newOffset)
    window.scrollTo(0, 0)
  }

  const handleDeleteClick = (review) => {
    if (window.confirm("Are you sure you want to --DELETE-- this review?")) {
      dispatch(deleteReview(review.id))
        .then(() => {
          console.log("Review Deleted Successfully")
          dispatch(fetchReviews())
        })
        .catch((error) => {
          console.error("Error Deleting Review:", error)
        })
    }
  }

  const handleRatingFilterChange = (event) => {
    setSelectedFilter(
      event.target.value === "All" ? null : Number(event.target.value)
    )
  }

  const filteredReviews = selectedFilter
    ? reviews.filter((review) => review.rating === selectedFilter)
    : reviews

  const currentReviews = filteredReviews.slice(
    itemOffset,
    itemOffset + itemsPerPage
  )
  const pageCount = Math.ceil(filteredReviews.length / itemsPerPage)

  return (
    <div className="adminReviewsPageContainer">
      <AdminReviewsChart />
      <h1 className="adminReviewsPageHeader">Reviews</h1>
      <div className="adminReviewsPageFilters">
        <div className="formControl">
          <label className="label">
            <span className="labelText">Rating</span>
          </label>
          <select
            className="selectInput"
            onChange={handleRatingFilterChange}
            defaultValue="All"
          >
            <option>All</option>
            <option value="1">☆</option>
            <option value="2">☆☆</option>
            <option value="3">☆☆☆</option>
            <option value="4">☆☆☆☆</option>
            <option value="5">☆☆☆☆☆</option>
          </select>
        </div>
      </div>

      <div className="adminReviewsPageTableWrapper">
        <table className="adminReviewsTable">
          <thead>
            <tr>
              <th>User</th>
              <th>Product</th>
              <th>Rating</th>
              <th>Review</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentReviews.length > 0 ? (
              currentReviews.map((review) => {
                const isExpanded = expandedReviews.has(review.id)
                const words = review.description.split(" ")
                const shouldTruncate = words.length > 8
                const displayedText =
                  isExpanded || !shouldTruncate
                    ? review.description
                    : words.slice(0, 8).join(" ") + "... "

                return (
                  <tr key={review.id} className="reviewRow">
                    <td>
                      <div className="userContainer">
                        <div className="avatarContainer">
                          <img
                            loading="lazy"
                            src={review.user.avatar}
                            alt={review.user.username}
                          />
                        </div>
                        <div className="username">{review.user.username}</div>
                      </div>
                    </td>
                    <td>
                      <Link
                        className="productLink"
                        to={`/products/${review.productId}`}
                      >
                        {review.product.name}
                      </Link>
                    </td>
                    <td>
                      <Suspense fallback={<Spinner />}>
                        <Rating rating={review.rating} />
                      </Suspense>
                    </td>
                    <td>
                      <span className="reviewDescription">{displayedText}</span>
                      {shouldTruncate && (
                        <button
                          className="readMoreButton"
                          onClick={() => toggleReviewExpansion(review.id)}
                        >
                          {isExpanded ? (
                            <>
                              <ChevronsLeft size={20} />
                              <span>Read Less</span>
                            </>
                          ) : (
                            <>
                              <span>Read More</span>
                              <ChevronsRight size={20} />
                            </>
                          )}
                        </button>
                      )}
                    </td>
                    <th>
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteClick(review)}
                      >
                        <svg className="delete-svgIcon" viewBox="0 0 448 512">
                          <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"></path>
                        </svg>
                      </button>
                    </th>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan="5">
                  <Spinner />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <ReactPaginate
          breakLabel="..."
          nextLabel={<ArrowBigRightDashIcon size={24} />}
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel={<ArrowBigLeftDashIcon size={24} />}
          className="pagination"
        />
      </div>
    </div>
  )
}

export default AdminReviewsPage
